import {
  ActionTableCellProps,
  ActionTableCellVariant,
} from '../Table/SimpleTable/TableCell/ActionsTableCell/ActionTableCell/ActionTableCell';
import { DataResponse } from '../../types/Config';
import _ from 'lodash';
import {
  InputConfig,
  InputValue,
  InputValueType,
} from '../../hooks/useForm/useForm';
import {
  getLocalStorageValue,
  LocalStorageKey,
} from '../../utils/localStorage/localStorage';
import { SortDirection } from '../Table/SimpleTable/TableHeadCell/SortableTableHeadCell/SortableTableHeadCell';
import dayjs from 'dayjs';
import { ScheduleViewType } from './ListSchedule/ListScheduleContent/ScheduleView/ScheduleView';
import {
  TableRowActionProps,
  TableRowActionVariant,
} from '../Table/SimpleTable/TableRow/TableRowAction/TableRowAction';

export type ParentObjectType = {
  name: string;
  objectIdentifier?: string;
  label?: string;
  value?: any;
  object?: any;
};

export enum RedirectParams {
  'redirectTo' = 'redirectTo',
  'label' = 'label',
}

export enum ActionClick {
  'delete' = 'delete',
  'edit' = 'edit',
  'navigate' = 'navigate',
}

const isActionVisible = (
  singleItem?: any,
  rowAction?: ActionTableCellProps | TableRowActionProps,
) => {
  const visibilityConfig = rowAction?.visibilityConfig;

  if (!visibilityConfig) {
    return true;
  }

  if (visibilityConfig?.specificValue === false) {
    return false;
  }

  if (
    visibilityConfig?.specificValue === true &&
    Object.keys(visibilityConfig).length === 1
  ) {
    return true;
  }

  const relevantValue = getObjectValue(singleItem, visibilityConfig.field);

  if (visibilityConfig.not) {
    if (visibilityConfig.values) {
      return !visibilityConfig.values.includes(relevantValue?.toString());
    }

    return relevantValue?.toString() !== visibilityConfig.value?.toString();
  }

  if (visibilityConfig.values) {
    return visibilityConfig.values.includes(relevantValue?.toString());
  }

  return relevantValue?.toString() === visibilityConfig.value?.toString();
};

export const mapRowActions = (
  id?: string,
  singleItem?: any,
  baseApiUrl?: string,
  onSuccessCallback?: () => void,
  rowActions?: ActionTableCellProps[],
  onLocalAction?: (type: ActionClick, value: InputValueType) => void,
): ActionTableCellProps[] => {
  return (
    (rowActions
      ?.map((rowAction) => {
        return mapSingleRowAction(
          rowAction,
          id,
          singleItem,
          baseApiUrl,
          onSuccessCallback,
          onLocalAction,
        );
      })
      .filter((action) => !!action) as ActionTableCellProps[]) ?? []
  );
};

export const getViableTableRowAction = (
  rowAction: TableRowActionProps | TableRowActionProps[],
  singleItem: any,
): TableRowActionProps => {
  if (!Array.isArray(rowAction)) {
    return rowAction;
  }

  return rowAction.find((singleRowAction) => {
    if (!singleRowAction.visibilityConfig) {
      return true;
    }

    return (
      getObjectValue(singleItem, singleRowAction.visibilityConfig.field) ===
      singleRowAction.visibilityConfig.value
    );
  }) as TableRowActionProps;
};

export const mapSingleRowAction = (
  rowAction: ActionTableCellProps | TableRowActionProps,
  id?: string,
  singleItem?: any,
  baseApiUrl?: string,
  onSuccessCallback?: () => void,
  onLocalAction?: (type: ActionClick, value?: InputValueType) => void,
) => {
  if (!isActionVisible(singleItem, rowAction)) {
    return null;
  }

  if (rowAction.variant === ActionTableCellVariant.edit) {
    return {
      ...rowAction,
      variant: rowAction.variant,
      to: rowAction.to.replace(':id', id ?? ''),
      onSuccess: onSuccessCallback,
      itemId: id,
      baseApiUrl: baseApiUrl,
      parentObject: rowAction.parentObject
        ? {
            ...rowAction.parentObject,
            label: singleItem
              ? getObjectValue(
                  singleItem,
                  rowAction.parentObject.objectIdentifier,
                )
              : '',
            value: getObjectValue(singleItem, 'id') ?? '',
          }
        : undefined,
    };
  }

  if (rowAction.variant === ActionTableCellVariant.modal) {
    return {
      ...rowAction,
      itemId: id,
      variant: rowAction.variant,
      onSuccess: onSuccessCallback,
      baseApiUrl: baseApiUrl,
      parentObject: singleItem,
      additionalParams: {
        [rowAction.parentObjectKey ?? '']: singleItem
          ? getObjectValue(singleItem, 'id') ?? ''
          : '',
      },
      onValueUpdated: (value: InputValueType) =>
        onLocalAction?.(ActionClick.edit, value),
    };
  }

  if (rowAction.variant === ActionTableCellVariant.modalConfirmation) {
    return {
      ...rowAction,
      baseApiUrl: baseApiUrl,
      apiUrl: rowAction.apiUrl.replace(':id', id ?? ''),
      onSuccess: (data: any) => {
        rowAction.onSuccess?.(data);
        onSuccessCallback?.();
      },
    };
  }

  if (rowAction.variant === ActionTableCellVariant.download) {
    if (rowAction?.isGeneric) {
      return {
        ...rowAction,
        baseApiUrl: baseApiUrl,
        apiUrl: rowAction.apiUrl,
        onSuccess: () => {
          onSuccessCallback?.();
        },
      };
    }

    const idValue =
      getObjectValue(singleItem, rowAction.valueKey ?? 'id') ?? id;

    if (!idValue) {
      return undefined;
    }

    return {
      ...rowAction,
      id: idValue,
    };
  }

  if (rowAction.variant === ActionTableCellVariant.pdfPreview) {
    const idValue =
      getObjectValue(singleItem, rowAction.valueKey ?? 'id') ?? id;

    if (!idValue) {
      return undefined;
    }

    return {
      ...rowAction,
      id: idValue,
      onSuccess: onSuccessCallback,
    };
  }

  if (rowAction.variant === TableRowActionVariant.pdfPreview) {
    const idValue =
      getObjectValue(singleItem, rowAction.valueKey ?? 'id') ?? id;

    if (!idValue) {
      return undefined;
    }

    return {
      ...rowAction,
      id: idValue,
      onSuccess: onSuccessCallback,
    };
  }

  if (rowAction.variant === TableRowActionVariant.navigation) {
    const idValue =
      getObjectValue(singleItem, rowAction.valueKey ?? 'id') ?? id;

    if (!idValue) {
      return undefined;
    }

    return {
      ...rowAction,
      id: idValue,
      onSuccess: onSuccessCallback,
      onClick: () => onLocalAction?.(ActionClick.navigate),
    };
  }

  if (rowAction.variant === ActionTableCellVariant.navigation) {
    const idValue =
      getObjectValue(singleItem, rowAction.valueKey ?? 'id') ?? id;

    return {
      ...rowAction,
      variant: rowAction.variant,
      to: rowAction.to.replace(':id', idValue ?? ''),
      onSuccess: onSuccessCallback,
      itemId: id,
      baseApiUrl: baseApiUrl,
      parentObject: rowAction.parentObject
        ? {
            ...rowAction.parentObject,
            label: singleItem
              ? getObjectValue(
                  singleItem,
                  rowAction.parentObject.objectIdentifier,
                )
              : '',
            value: getObjectValue(singleItem, 'id') ?? '',
            object: singleItem,
          }
        : undefined,
    };
  }

  if (rowAction.variant === ActionTableCellVariant.remove) {
    const idValue = getObjectValue(singleItem, 'id') ?? id;

    return {
      ...rowAction,
      onClick: (value: InputValueType) =>
        onLocalAction?.(ActionClick.delete, value),
      id: idValue,
    };
  }

  return {
    ...rowAction,
    baseApiUrl: baseApiUrl,
    apiUrl: rowAction.apiUrl.replace(':id', id ?? ''),
    onSuccess: () => {
      onSuccessCallback?.();
      rowAction?.onSuccess?.();
    },
  };
};

export const getObjectValue = (singleItem: DataResponse, key?: string): any => {
  if (!key) {
    return '';
  }

  if (key.includes(',')) {
    return key
      .split(',')
      .map((singleKey) => getSingleObjectValue(singleItem, singleKey))
      .join(' ');
  }

  return getSingleObjectValue(singleItem, key);
};

export const getSingleObjectValue = (
  singleItem: DataResponse,
  key?: string,
): any => {
  if (!key) {
    return singleItem;
  }

  const firstKey = _.first(key.split('.'));

  if (!firstKey) {
    return '';
  }

  if (firstKey.includes('[]')) {
    const value = singleItem[firstKey.replace('[]', '')];

    if (_.isArray(value)) {
      return value.map((singleValue) =>
        getSingleObjectValue(singleValue, key.replace(`${firstKey}.`, '')),
      );
    }
  }

  const value = singleItem[firstKey];

  if (_.isObject(value) && !Array.isArray(value)) {
    return getSingleObjectValue(value, trimKey(key, firstKey));
  }

  return value ?? '';
};

const trimKey = (key: string, firstKey: string) => {
  if (key.includes(`${firstKey}.`)) {
    return key.replace(`${firstKey}.`, '');
  }

  return key.replace(firstKey, '');
};

export const getInitialPageValue = (
  ignoreQuery: boolean,
  searchParams: URLSearchParams,
  apiUrl: LocalStorageKey,
  id?: string,
): number => {
  if (ignoreQuery) {
    return 0;
  }

  const searchParamsValue = searchParams.get('page');

  if (searchParamsValue) {
    return parseInt(searchParamsValue);
  }

  if (getLocalStorageValue(LocalStorageKey.listPage) !== apiUrl) {
    return 0;
  }

  return parseInt(
    getLocalStorageValue<{ page: string }>(
      `${apiUrl}${id ?? ''}` as LocalStorageKey,
    )?.page || '0',
  );
};

export const getInitialRowsPerPageValue = (
  ignoreQuery: boolean,
  searchParams: URLSearchParams,
  apiUrl: LocalStorageKey,
  defaultRowsPerPage: number | undefined = 10,
  id?: string,
): number => {
  if (ignoreQuery) {
    return defaultRowsPerPage;
  }

  if (getLocalStorageValue(LocalStorageKey.listPage) !== apiUrl) {
    return defaultRowsPerPage;
  }

  return parseInt(
    searchParams.get('rowsPerPage') ||
      getLocalStorageValue<{ rowsPerPage: string }>(
        `${apiUrl}${id ?? ''}` as LocalStorageKey,
      )?.rowsPerPage ||
      defaultRowsPerPage?.toString(),
  );
};

export const getInitialScrollPosition = (
  ignoreQuery: boolean,
  searchParams: URLSearchParams,
  apiUrl: LocalStorageKey,
  id?: string,
): number => {
  if (ignoreQuery) {
    return 0;
  }

  if (getLocalStorageValue(LocalStorageKey.listPage) !== apiUrl) {
    return 0;
  }

  return parseInt(
    searchParams.get('scrollPosition') ||
      getLocalStorageValue<{ scrollPosition: string }>(
        `${apiUrl}${id ?? ''}` as LocalStorageKey,
      )?.scrollPosition ||
      '0',
  );
};

export const getInitialSelectedTab = (
  ignoreQuery: boolean,
  searchParams: URLSearchParams,
  apiUrl: LocalStorageKey,
  id?: string,
): string | undefined => {
  if (ignoreQuery) {
    return undefined;
  }

  if (getLocalStorageValue(LocalStorageKey.listPage) !== apiUrl) {
    return undefined;
  }

  return (
    searchParams.get('selectedTab') ||
    getLocalStorageValue<{ selectedTab: string }>(
      `${apiUrl}${id ?? ''}` as LocalStorageKey,
    )?.selectedTab
  );
};

export const getInitialSortValue = (
  ignoreQuery: boolean,
  searchParams: URLSearchParams,
  apiUrl: LocalStorageKey,
  id?: string,
): string | undefined => {
  if (ignoreQuery) {
    return undefined;
  }

  if (getLocalStorageValue(LocalStorageKey.listPage) !== apiUrl) {
    return undefined;
  }

  return (
    searchParams.get('sortBy') ||
    getLocalStorageValue<{ sortBy: string }>(
      `${apiUrl}${id ?? ''}` as LocalStorageKey,
    )?.sortBy
  );
};

export const getInitialSortDirectionValue = (
  ignoreQuery: boolean,
  searchParams: URLSearchParams,
  apiUrl: LocalStorageKey,
  id?: string,
): SortDirection | undefined => {
  if (ignoreQuery) {
    return undefined;
  }

  if (getLocalStorageValue(LocalStorageKey.listPage) !== apiUrl) {
    return undefined;
  }

  return (
    ((searchParams.get('sortDirection') ||
      getLocalStorageValue<{ sortDirection: string }>(
        `${apiUrl}${id ?? ''}` as LocalStorageKey,
      )?.sortDirection) as SortDirection) ?? undefined
  );
};

export const getInitialDateValue = (
  ignoreQuery: boolean,
  searchParams: URLSearchParams,
  apiUrl: LocalStorageKey,
  id?: string,
): dayjs.Dayjs | undefined => {
  if (ignoreQuery) {
    return undefined;
  }

  const searchParamsValue = searchParams.get('date');

  if (searchParamsValue) {
    return dayjs(searchParamsValue);
  }

  if (getLocalStorageValue(LocalStorageKey.listPage) !== apiUrl) {
    return undefined;
  }

  const valueFromLocalStorage =
    getLocalStorageValue<{ date: string }>(
      `${apiUrl}${id ?? ''}` as LocalStorageKey,
    )?.date ?? undefined;

  if (!valueFromLocalStorage) {
    return undefined;
  }

  return dayjs(valueFromLocalStorage);
};

export const getInitialActiveViewValue = (
  ignoreQuery: boolean,
  searchParams: URLSearchParams,
  apiUrl: LocalStorageKey,
  id?: string,
): ScheduleViewType | undefined => {
  if (ignoreQuery) {
    return undefined;
  }

  if (getLocalStorageValue(LocalStorageKey.listPage) !== apiUrl) {
    return undefined;
  }

  const valueFromLocalStorage =
    (searchParams.get('activeView') ||
      getLocalStorageValue<{ activeView: string }>(
        `${apiUrl}${id ?? ''}` as LocalStorageKey,
      )?.activeView) ??
    undefined;

  if (!valueFromLocalStorage) {
    return undefined;
  }

  return valueFromLocalStorage as ScheduleViewType;
};

export const getInitialFilter = (
  ignoreQuery: boolean,
  searchParams: URLSearchParams,
  apiUrl: LocalStorageKey,
  filters?: InputConfig[],
  id?: string,
) => {
  if (!filters) {
    return undefined;
  }

  if (ignoreQuery) {
    return [...filters];
  }

  if (getLocalStorageValue(LocalStorageKey.listPage) !== apiUrl) {
    return [...filters];
  }

  const initialFilters = [...filters];
  const localStorageValue = getLocalStorageValue<any>(
    `${apiUrl}${id ?? ''}` as LocalStorageKey,
  );

  initialFilters?.forEach((singleFilter) => {
    const searchParam = searchParams.get(singleFilter.name);

    if (!searchParam) {
      if (!localStorageValue) {
        singleFilter.defaultValue = null;
        return;
      }

      if (
        // @ts-ignore
        singleFilter.inputFactoryProps?.isMultiple &&
        localStorageValue[singleFilter.name]
      ) {
        singleFilter.defaultValue =
          localStorageValue[singleFilter.name].split(',');
      } else {
        singleFilter.defaultValue = localStorageValue[singleFilter.name];
      }

      return;
    }

    // @ts-ignore
    if (singleFilter.inputFactoryProps?.isMultiple) {
      singleFilter.defaultValue = searchParam.split(',');
    } else {
      singleFilter.defaultValue = searchParam;
    }
  });

  return initialFilters;
};

export const getFilterParams = (
  searchParams: URLSearchParams,
  page: number,
  rowsPerPage: number,
  inputValues: InputValue[],
  sortBy?: string,
  sortDirection?: SortDirection,
  selectedTab?: string,
  activeTab?: string,
  date?: dayjs.Dayjs,
) => {
  const params: any = {
    ...Object.fromEntries(searchParams.entries()),
    page: page.toString(),
    rowsPerPage: rowsPerPage.toString(),
    scrollPosition: window.scrollY.toString(),
    sortBy: sortBy ?? '',
    sortDirection: sortDirection ?? '',
    selectedTab: selectedTab ?? '',
    activeTab: activeTab ?? '',
    date: date ?? '',
  };

  inputValues.forEach((inputValue) => {
    params[inputValue.name] = Array.isArray(inputValue.value)
      ? inputValue.value.join(',')
      : inputValue.value?.toString() ?? '';
  });

  return params;
};

export const getInitialTabValue = (searchParams: URLSearchParams): number => {
  return parseInt(searchParams.get('activeTab') || '0');
};

export const getKanbanFilterParams = (
  searchParams: URLSearchParams,
  inputValues: InputValue[],
  activeTab?: string,
) => {
  const params: any = {
    ...Object.fromEntries(searchParams.entries()),
    activeTab: activeTab ?? '',
  };

  inputValues.forEach((inputValue) => {
    if (inputValue.value) {
      params[inputValue.name] = Array.isArray(inputValue.value)
        ? inputValue.value.join(',')
        : inputValue.value;
    } else {
      searchParams.delete(inputValue.name);
    }
  });

  return params;
};

export const getScheduleFilterParams = (
  searchParams: URLSearchParams,
  date: dayjs.Dayjs,
  activeView: ScheduleViewType,
  inputValues: InputValue[],
  activeTab?: string,
) => {
  const params: any = {
    ...Object.fromEntries(searchParams.entries()),
    activeTab: activeTab ?? '',
    date: date.toISOString(),
    activeView: activeView,
  };

  inputValues.forEach((inputValue) => {
    if (inputValue.value) {
      params[inputValue.name] = Array.isArray(inputValue.value)
        ? inputValue.value.join(',')
        : inputValue.value;
    } else {
      searchParams.delete(inputValue.name);
    }
  });

  return params;
};
